import { useQuery } from "@tanstack/react-query";
import type { Environment } from "../../../types";
import type { StakeRewardRecord, StakeRewardsResponse } from "../types";
import { getApiBase } from "../../../lib/remote-config";
import { pickAccruedCumulativeEarned } from "../utils";

export const STAKE_REWARDS_QUERY_KEY = "wp:stake-rewards";

/** 개인 실현 APR 분자 계산에 충분한 일자 버킷 확보용 조회 한도(약 1개월). */
const REWARDS_HISTORY_LIMIT = 31;

/**
 * POSA `GET /rewards/:address?limit=31` — 최근 리워드 스냅샷을 조회한다.
 * 응답이 최신순 정렬이라 `latestReward`(첫 레코드)가 최신이며, `aprAccrued`는
 * posa 포트폴리오 `pickChartAccrued`와 동일하게 일자 버킷팅 후 오늘(없으면
 * 최신일) 버킷의 `cumulative_earned`를 고른 값으로, 개인 실현 APR 분자로 쓴다.
 * (`/stake/:address`의 `rewards_earned`는 staging에서 항상 0이라 쓰지 않는다.)
 */
export function useStakeLatestReward(
  env: Environment,
  walletAddress: string,
  enabled = true,
) {
  const isEnabled = enabled && !!walletAddress;

  const { data, ...rest } = useQuery<StakeRewardRecord[]>({
    queryKey: [STAKE_REWARDS_QUERY_KEY, env, walletAddress],
    queryFn: async ({ signal }) => {
      const baseUrl = await getApiBase("posa", env);
      const res = await fetch(
        `${baseUrl}/rewards/${walletAddress}?limit=${REWARDS_HISTORY_LIMIT}`,
        { cache: "no-store", signal },
      );
      if (!res.ok) throw new Error(`Stake rewards fetch failed: ${res.status}`);
      const json: StakeRewardsResponse = await res.json();
      // POSA envelope: 성공 코드는 0 또는 200.
      if (json.code !== 0 && json.code !== 200) {
        throw new Error(`Stake rewards API error: ${json.code}`);
      }
      return json.data?.data ?? [];
    },
    enabled: isEnabled,
    staleTime: 0,
  });

  return {
    latestReward: data?.[0] ?? null,
    aprAccrued: pickAccruedCumulativeEarned(data),
    ...rest,
  };
}
